import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import log from "loglevel"
import { NexPage } from "../components/Table/pageable-table/NexPage"
import { generateSearchParamsForDjango } from "../components/Table/pageable-table/PagingFilters"
import { SensorType } from "../models/sensors/Sensor"
import { CreateSensorInstallInfo, SensorInstallInfo } from "../models/sensors/SensorInstallInfo"
import { SensorInstallFiltersAndPaging } from "../pages/SensorInstallation/table/SensorInstallSearchFilters"
import { prettyJson } from "../tools"
import { API, NexusResponse } from "./API"
import { QueryKeys } from "./QueryConfig"

export const useFindSensor = (sensor_id?: string) => {
    return useQuery({
        queryKey: [QueryKeys.FIND_ONE_SENSOR, sensor_id],
        queryFn: async () => {
            const sensorResults: NexusResponse<SensorType> = await API.get(
                `/sensors/${sensor_id}`,
                `Unable to fetch sensor with id ${sensor_id}`,
            )
            return sensorResults.data
        },
        enabled: !!sensor_id,
    })
}

const sensorParamOrder: (keyof SensorType)[] = ["sensor_model", "serial_number", "sensor_id"]
const sensorQueryParams = new URLSearchParams({ ordering: sensorParamOrder.join(",") })

export const useFindSensors = () => {
    return useQuery({
        queryKey: [QueryKeys.FIND_SENSORS],
        queryFn: async () => {
            const sensorResults: NexusResponse<SensorType[]> = await API.get(
                `/sensors?${sensorQueryParams.toString()}`,
                "Unable to fetch sensor list",
            )
            return sensorResults.data
        },
    })
}

export const useFindRecentSensorInstallations = (filters: SensorInstallFiltersAndPaging) => {
    return useQuery({
        queryKey: [QueryKeys.FIND_SENSOR_INSTALLATIONS, filters],
        queryFn: async () => {
            const searchParams = generateSearchParamsForDjango(filters)
            // this endpoint will allways have the most_recent=True query
            searchParams.append("most_recent", "True")
            const result: NexusResponse<NexPage<SensorInstallInfo>> = await API.get(
                `/sensor-install?${searchParams.toString()}`,
                `Couldn't fetch sensor installations with filters ${prettyJson(filters)}`,
            )
            return result.data
        },
        refetchOnWindowFocus: false,
    })
}

export const useFindSensorInstallationsForSensor = (filters: SensorInstallFiltersAndPaging, sensor?: string) => {
    return useQuery({
        queryKey: [QueryKeys.FIND_SENSOR_INSTALLATIONS, filters, sensor],
        queryFn: async () => {
            const searchParams = generateSearchParamsForDjango(filters)
            // this endpoint will always have the sensor=sensor_id query
            searchParams.append("sensor", sensor!)
            const result: NexusResponse<NexPage<SensorInstallInfo>> = await API.get(
                `/sensor-install?${searchParams.toString()}`,
                `Couldn't fetch sensor installations for sensor ${sensor} with filters ${prettyJson(filters)}`,
            )
            return result.data
        },
        refetchOnWindowFocus: false,
        enabled: !!sensor,
    })
}

export const useCreateSensorInstall = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (sensorInstallPayload: CreateSensorInstallInfo) => {
            await API.post("/sensor-install", sensorInstallPayload, "Couldn't submit sensor installation.")
            await queryClient.invalidateQueries({ queryKey: [QueryKeys.FIND_SENSOR_INSTALLATIONS] })
        },
    })
}

export const useEditSensorInstall = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (sensorInstallPayload: CreateSensorInstallInfo) => {
            // Within nexus when updating an existing entry we must use PUT instead of POST
            await API.put(
                `/sensor-install/${sensorInstallPayload.id}`,
                sensorInstallPayload,
                `Couldn't update sensor installation with id ${sensorInstallPayload.id}.`,
            )
            await queryClient.invalidateQueries({ queryKey: [QueryKeys.FIND_SENSOR_INSTALLATIONS] })
        },
    })
}

export const useDeleteSensorInstall = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (sensorInstallId: number): Promise<void> => {
            log.info(`Delete Sensor Install Mutation with id ${sensorInstallId}`)
            await API.delete(
                `/sensor-install/${sensorInstallId}`,
                `Couldn't delete sensor installation with id ${sensorInstallId}.`,
            )
            await queryClient.invalidateQueries({ queryKey: [QueryKeys.FIND_SENSOR_INSTALLATIONS] })
        },
    })
}
